import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { PenBox } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../common/table/CommonTable";
import {
  fetchAdmissions,
  resetCurrentAdmission,
  updateAdmission,
} from "../../redux/admissions/admissionsActions";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchFloors } from "../../redux/floor/floorAction";
import { fetchWards } from "../../redux/ward/wardAction";

const AdmissionsList = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAdmissionsAll || {}
  );

  const updateAdmissionState = useSelector(
    (state) => state?.admission?.updateAdmission || {}
  );
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Advanced Filter States
  const [filterBlock, setFilterBlock] = useState("");
  const [filterFloor, setFilterFloor] = useState("");
  const [filterWard, setFilterWard] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Specific Search States
  const [searchIP, setSearchIP] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchPhone, setSearchPhone] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [dischargeType, setDischargeType] = useState("");
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

  const blockData = useSelector((state) => state.block?.fetchBlocks || {});
  const floorData = useSelector((state) => state.floor?.fetchFloors || {});
  const wardData = useSelector((state) => state.ward?.fetchWards || {});
  const totalPages = admissionsList?.admissions?.totalPages || 0;
  const [rowsPerPage] = useState(15);
  const searchTimeoutRef = useRef(null);
  const filterTimeoutRef = useRef(null);



  const dischargeOptions = [
    { value: "3", label: "Discharged" },
    { value: "4", label: "Death" },
    { value: "5", label: "Abscond" },
    { value: "6", label: "Referral" },
    {value: "7", label: "AMA"}
  ];

  const fetchData = (page) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const payload = {
      pageNo: page,
      pagesize: rowsPerPage,
      pagination_required: true,
      search: searchTerm,
      block_id: filterBlock,
      floor_id: filterFloor,
      ward_id: filterWard,
      start_date: startDate && new Date(startDate) <= today ? new Date(startDate).toLocaleDateString('en-GB').replace(/\//g, '-') : "",
      end_date: endDate && new Date(endDate) <= today ? new Date(endDate).toLocaleDateString('en-GB').replace(/\//g, '-') : "",
      ip_number: searchIP,
      patient_name: searchName,
      phone: searchPhone
    };

    dispatch(fetchAdmissions(payload));
  };

  useEffect(() => {
    dispatch(fetchBlocks());
  }, [dispatch]);

  useEffect(() => {
    if (filterBlock) {
      dispatch(fetchFloors(filterBlock));
      setFilterFloor("");
      setFilterWard("");
    }
  }, [filterBlock, dispatch]);

  useEffect(() => {
    if (filterBlock && filterFloor) {
      dispatch(fetchWards(filterBlock, filterFloor));
      setFilterWard("");
    }
  }, [filterBlock, filterFloor, dispatch]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchData(currentPage);
  }, [
    currentPage, 
    dispatch
  ]);

  const handleSearchClick = () => {
    setCurrentPage(1);
    fetchData(1);
  };

  const handleResetFilters = () => {
    setFilterBlock("");
    setFilterFloor("");
    setFilterWard("");
    setStartDate("");
    setEndDate("");
    setSearchIP("");
    setSearchName("");
    setSearchPhone("");
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
    
    // Trigger fetch with cleared params
    const payload = {
      pageNo: 1,
      pagesize: rowsPerPage,
      pagination_required: true,
      search: "",
      block_id: "",
      floor_id: "",
      ward_id: "",
      start_date: "",
      end_date: "",
      ip_number: "",
      patient_name: "",
      phone: ""
    };
    dispatch(fetchAdmissions(payload));
  };

  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.admission_id,
        admissionNo: admission.admission_no,
        patientId: admission.patient_id,
        patientSerialNo: admission.patient_serial_no || "N/A",
        patientName: admission.patient_name,
        ipNumber: admission.ip_number,
        phone: admission.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockName: admission.block || "N/A",
        floorName: admission.floor || "N/A",
        departmentName: admission.department || "N/A",
        wardName: admission.ward || "N/A",
        status: getStatusLabel(admission.status),
        statusCode: admission.status,
        numAttenders: admission.attenders || 0,
        attenders: admission.Attenders || [],
      }));

      setData(formattedData);

      const lowercasedSearchTerm = searchTerm?.toLowerCase() || "";
      const filtered = (searchTerm || searchIP || searchName || searchPhone)
        ? formattedData.filter(
          (admission) =>
            (searchTerm && (
              admission.patientName.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.ipNumber.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.patientSerialNo.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.blockName.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.floorName.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.wardName.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.departmentName.toLowerCase().includes(lowercasedSearchTerm)
            )) ||
            (searchIP && admission.ipNumber?.toLowerCase().includes(searchIP.toLowerCase())) ||
            (searchName && admission.patientName?.toLowerCase().includes(searchName.toLowerCase())) ||
            (searchPhone && admission.phone?.toLowerCase().includes(searchPhone.toLowerCase()))
        )
        : formattedData;

      // Clear any existing timeout
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }

      // Set filtered data immediately
      setFilteredData(filtered);
    } else {
      setData([]);
      setFilteredData([]);
    }
  }, [admissionsList, searchTerm]);

  useEffect(() => {
    if (updateAdmissionState.success) {
      setSuccessSnackbarOpen(true);
      dispatch(resetCurrentAdmission());
      fetchData(currentPage);
    }
  }, [updateAdmissionState.success, dispatch]);

  const getStatusLabel = (statusCode) => {
    switch (statusCode) {
      case 1:
        return "Active";
      case 2:
        return "In Active";
      case 3:
        return "Discharged";
      case 4:
        return "Death";
      case 5:
        return "Abscond";
      case 6:
        return "Referral";
      default:
        return "Unknown";
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchInputValue(event.target.value);
    setSearchTerm(event.target.value);
  };

  const handleViewClick = (row) => {
    navigate(`/admission/view/${row.patientId}`);
  };

  const handleEditClick = (row) => {
    navigate(`/admission/edit/${row.id}`);
  };

  const handleDischargeClick = (row) => {
    if (row.statusCode === 1) {
      setSelectedAdmission(row);
      setDischargeType("");
      setDischargeDialogOpen(true);
    }
  };

  const handleDischargeConfirm = () => {
    if (selectedAdmission && dischargeType) {
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(
        2,
        "0"
      )} ${String(currentDate.getHours()).padStart(2, "0")}:${String(
        currentDate.getMinutes()
      ).padStart(2, "0")}`;

      const formdata = {
        admission_status: dischargeType,
        discharge_date: formattedDate,
      };

      dispatch(updateAdmission(selectedAdmission.id, formdata));
      setDischargeDialogOpen(false);
      setDischargeType("");
      setSelectedAdmission(null);
    }
  };

  const handleDischargeCancel = () => {
    setDischargeDialogOpen(false);
    setSelectedAdmission(null);
    setDischargeType("");
  };

  const handleDischargeTypeChange = (event) => {
    setDischargeType(event.target.value);
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };


  const getDischargeTypeLabel = (value) => {
    const option = dischargeOptions.find(opt => opt.value === value);
    return option ? option.label : "Updated";
  };

  const getBlockOptions = () => {
    return blockData?.Blocks?.data?.map((block) => ({
      value: block.id,
      label: block.block_name,
    })) || [];
  };

  const getFloorOptions = (blockId) => {
    return floorData?.floors?.data?.map((floor) => ({
      value: floor.id,
      label: floor.floor_name,
    })) || [];
  };

  const getWardOptions = () => {
    return wardData?.wards?.data?.map((ward) => ({
      value: ward.id,
      label: ward.ward_name,
    })) || [];
  };

  const columns = [
    {
      id: "admissionNo",
      label: "Admission No",
      sortable: true,
      render: (value, row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "dodgerblue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => handleViewClick(row)}
        >
          {value}
        </Typography>
      ),
    },
    { id: "patientName", label: "Patient Name", sortable: true },
    { id: "ipNumber", label: "IP Number", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    {
      id: "admitDate",
      label: "Admit Date",
      sortable: true,
      render: (value) => {
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    {
      id: "dischargeDate",
      label: "Discharge Date",
      sortable: true,
      render: (value) => {
        if (!value) return "Not Discharged";
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    { id: "blockName", label: "Block", sortable: true },
    { id: "floorName", label: "Floor", sortable: true },
    { id: "departmentName", label: "Department", sortable: true },
    { id: "wardName", label: "Ward", sortable: true },
    { id: "numAttenders", label: "Attenders", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "In Active"
                ? "default"
                : value === "Discharged"
                  ? "primary"
                  : "warning"
          }
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      sortable: false,
      render: (_, row) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
          {row.statusCode !== 3 && (
            <Button
              color="blue"
              onClick={() => handleViewClick(row)}
              size="small"
              title="View"
              startIcon={
                <VisibilityIcon
                  sx={{
                    color: "dodgerblue",
                  }}
                />
              }
              sx={{
                color: "dodgerblue",
              }}
            >
              View
            </Button>
          )}
          <Button
              color="green"
              onClick={() => handleEditClick(row)}
              size="small"
              title="View"
              startIcon={
                <PenBox
                  sx={{
                    color: "green",
                  }}
                />
              }
              sx={{
                color: "green",
              }}
            >
              Edit
            </Button>
          {row.statusCode === 1 && (
            <Button
              onClick={() => handleDischargeClick(row)}
              size="small"
              title="Discharge"
              startIcon={
                <LogoutIcon
                  sx={{
                    color: "red",
                  }}
                />
              }
              sx={{
                color: "red",
              }}
            >
              Discharge
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 ,color:"black" }}>
                Filter & Search Admissions
              </Typography>
              <Grid container spacing={2}>
                {/* Search Row */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="IP Number"
                    variant="outlined"
                    value={searchIP}
                    onChange={(e) => setSearchIP(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Patient Name"
                    variant="outlined"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    variant="outlined"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="General Search"
                    value={searchInputValue}
                    onChange={handleSearchInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>

                {/* Filter Row */}
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Block"
                    value={filterBlock}
                    onChange={(e) => setFilterBlock(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value=""></option>
                    {getBlockOptions().map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Floor"
                    value={filterFloor}
                    onChange={(e) => setFilterFloor(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                    disabled={!filterBlock}
                  >
                    <option value=""></option>
                    {getFloorOptions(filterBlock).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Ward"
                    value={filterWard}
                    onChange={(e) => setFilterWard(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                    disabled={!filterFloor}
                  >
                    <option value=""></option>
                    {getWardOptions().map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 1, gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleResetFilters}
                    sx={{ px: 4 }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleSearchClick}
                    startIcon={<SearchIcon />}
                    sx={{ px: 4 }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/admission/create")}
                    sx={{ px: 4 }}
                  >
                    Add Admission
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <ReusableTable
        title="Admissions"
        columns={columns}
        data={filteredData}
        isLoading={admissionsList.inProgress}
        headerColor="#000"
        rowColor="#ffffff"
        rowsPerPage={rowsPerPage}
        alternateRowColor="#f5f5f5"
        withPagination={true}
        withActions={false}
        showDownloadOption={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        emptyMessage="No records found"
      />

      <Dialog
        open={dischargeDialogOpen}
        onClose={handleDischargeCancel}
        aria-labelledby="discharge-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="discharge-dialog-title"
          sx={{ bgcolor: "red", color: "white" }}
        >
          Discharge Patient
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ mb: 3 }}>
            Please select discharge type for patient:
            {selectedAdmission && (
              <Box component="span" fontWeight="bold">
                {" "}
                {selectedAdmission.patientName}
              </Box>
            )}
          </Typography>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="discharge-type-label">Discharge Type</InputLabel>
            <Select
              labelId="discharge-type-label"
              id="discharge-type-select"
              value={dischargeType}
              onChange={handleDischargeTypeChange}
              label="Discharge Type"
            >
              {dischargeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDischargeCancel}
            sx={{
              backgroundColor: "orange",
              color: "white",
              "&:hover": {
                backgroundColor: "darkorange",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDischargeConfirm}
            variant="contained"
            disabled={!dischargeType}
            sx={{
              backgroundColor: dischargeType ? "green" : "grey",
              color: "white",
              "&:hover": {
                backgroundColor: dischargeType ? "darkgreen" : "grey",
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: "100%", bgcolor: "green", color: "white" }}
        >
          Patient {getDischargeTypeLabel(dischargeType)} successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdmissionsList;